import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/auth-context'; // Assuming auth-context.tsx is in src/context
import { Login } from './features/auth/login'; // Adjust path as needed
import RequestPasswordResetPage from './features/auth/RequestPasswordResetPage'; // Adjust path
import PasswordResetPage from './features/auth/PasswordResetPage'; // Adjust path
import EmailVerificationPage from './features/auth/EmailVerificationPage'; // Adjust path
import InvitationPage from './features/auth/InvitationPage'; // Invitation completion page
import InvitationPendingPage from './features/auth/InvitationPendingPage'; // Handle invitation pending state
import MainLayout from './layouts/MainLayout';
import { EnvironmentIndicator } from './components/layout/EnvironmentIndicator';
import TranslationChoiceBanner from './components/TranslationChoiceBanner';
import TranslationWarningBadge from './components/TranslationWarningBadge';
import useTranslationManager from './hooks/useTranslationManager';
import { UpdateBanner } from './components/UpdateBanner';
import { AppLoadingScreen } from './components/ui/AppLoadingScreen';

// Import TanStack Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FunctionsFetchError } from '@supabase/supabase-js';
import { AuthErrorInterceptor } from './lib/auth-error-interceptor';
import { setupGlobalAuthErrorHandler } from './lib/supabase-functions-wrapper';
import { componentLogger } from './lib/logging';

// Import relocated providers
import { ThemeProvider } from './context/theme-context';
import { FontProvider } from './context/font-context';
import { SearchProvider } from './context/search-context';
import { SidebarProvider } from './components/ui/sidebar';
import UsersProvider from './features/users/context/users-context';
import { PermissionProvider } from './lib/permissions/contexts/permission-provider';

// Create a QueryClient with global error handler and performance optimization
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // PERFORMANCE: Global cache configuration to prevent excessive refetching
      staleTime: 5 * 60 * 1000,           // 5 minutes - data stays fresh, reduces refetches
      cacheTime: 10 * 60 * 1000,          // 10 minutes - keep in memory longer
      refetchOnWindowFocus: false,         // Don't refetch when switching tabs
      refetchOnReconnect: false,           // Don't refetch on network reconnect

      // Global error handler for all queries
      onError: async (error: unknown) => {
        componentLogger.error('QueryClient', 'Query error', error as Error);

        // Use enhanced Supabase-specific error analysis
        let errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(error);

        // Fall back to generic analysis if needed
        if (!errorDetails) {
          errorDetails = AuthErrorInterceptor.analyzeError(error);
        }

        if (errorDetails) {
          componentLogger.debug('QueryClient', 'Auth error detected in query, will be handled by auth context');
          // The auth context will handle the error via its global error handler
          // We don't handle it here to avoid duplicate handling
        }
      },
      retry: (failureCount, error) => {
        // Don't retry auth errors, especially FunctionsFetchError (Edge Runtime JWT rejection)
        if (error instanceof FunctionsFetchError) {
          componentLogger.debug('QueryClient', 'Not retrying FunctionsFetchError (Edge Runtime JWT rejection)');
          return false;
        }

        // Use synchronous error analysis for retry logic
        const errorDetails = AuthErrorInterceptor.analyzeError(error);

        if (errorDetails) {
          componentLogger.debug('QueryClient', 'Not retrying query due to auth error', { errorType: errorDetails.type });
          return false;
        }

        // Default retry logic for non-auth errors
        return failureCount < 3;
      },
    },
    mutations: {
      // Global error handler for all mutations
      onError: async (error: unknown) => {
        componentLogger.error('QueryClient', 'Mutation error', error as Error);

        // Use enhanced Supabase-specific error analysis
        let errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(error);

        // Fall back to generic analysis if needed
        if (!errorDetails) {
          errorDetails = AuthErrorInterceptor.analyzeError(error);
        }

        if (errorDetails) {
          componentLogger.debug('QueryClient', 'Auth error detected in mutation, will be handled by auth context');
          // The auth context will handle the error via its global error handler
        }
      },
      retry: (_failureCount, error) => {
        // Don't retry auth errors for mutations, especially FunctionsFetchError
        if (error instanceof FunctionsFetchError) {
          componentLogger.debug('QueryClient', 'Not retrying mutation FunctionsFetchError');
          return false;
        }

        // Use synchronous error analysis for retry logic
        const errorDetails = AuthErrorInterceptor.analyzeError(error);
        if (errorDetails) {
          componentLogger.debug('QueryClient', 'Not retrying mutation due to auth error', { errorType: errorDetails.type });
          return false;
        }

        // Generally don't retry mutations to avoid duplicate operations
        return false;
      },
    },
  },
});

// Placeholder for your main application content after login - currently unused
// const HomePagePlaceholder: React.FC = () => {
//   const { user } = useAuth();
//   return (
//     <div style={{ padding: '20px' }}>
//       <h1>Welcome!</h1>
//       {user ? <p>You are logged in as: {user.email}</p> : <p>Loading user...</p>}
//       <p>This is a placeholder for your main application content.</p>
//       <button onClick={() => safeSignOut()}>Sign Out</button>
//     </div>
//   );
// };

interface ProtectedRouteProps {
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, session, isPasswordRecovery, authErrorDetails, clearAuthErrorDetails: _clearAuthErrorDetails } = useAuth();

  // Check if there are auth errors in the URL hash
  const urlHash = window.location.hash;
  const hasAuthError = urlHash.includes('error=') || urlHash.includes('error_code=');

  // CRITICAL FIX: Only block render during INITIAL mount (first load)
  // After that, render immediately and let auth resolve in background
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = React.useState(false);

  React.useEffect(() => {
    // Once we have any auth state (even if loading=false), mark as loaded
    if (!loading || user || session) {
      setHasInitiallyLoaded(true);
    }
  }, [loading, user, session]);

  // ONLY show loading on very first mount AND if we have no session data
  // This prevents the long "Loading authentication state..." hang
  if (!hasInitiallyLoaded && loading && !user && !session) {
    // Short timeout to prevent indefinite hanging
    setTimeout(() => setHasInitiallyLoaded(true), 3000);
    return <AppLoadingScreen />;
  }

  // Handle network errors differently - don't redirect, just show waiting message
  if (authErrorDetails && authErrorDetails.code === 'NETWORK_ERROR') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '20px', fontSize: '16px', color: '#666' }}>
          Connection lost
        </div>
        <div style={{ fontSize: '14px', color: '#999' }}>
          Waiting for network to reconnect...
        </div>
      </div>
    );
  }

  // If there's an auth error that requires redirect to login, show brief message
  if (authErrorDetails && authErrorDetails.shouldRedirectToLogin) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '20px', fontSize: '16px', color: '#666' }}>
          {authErrorDetails.userMessage || 'Authentication error occurred'}
        </div>
        <div style={{ fontSize: '14px', color: '#999' }}>
          Redirecting to login page...
        </div>
      </div>
    );
  }

  // Handle password recovery flow only (not invitations)
  // Password recovery uses hash-based URLs like #type=recovery
  if ((hasAuthError || isPasswordRecovery) && isPasswordRecovery) {
    // If user is authenticated and NOT in password recovery mode, they've completed the flow
    if (user && !isPasswordRecovery) {
      // User authenticated and not in password recovery - allowing normal flow
      // Clear the hash to avoid future redirects
      if (urlHash) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    } else {
      // Redirecting to password reset page for password recovery flow only
      return <Navigate to={`/password-reset${urlHash}`} replace />;
    }
  }
  
  if (!user) {
    const searchParams = window.location.search;
    return <Navigate to={`/login${searchParams}`} replace />;
  }
  
  return children;
};

const PublicRouteOnly: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, isPasswordRecovery } = useAuth();

  // CRITICAL FIX: Don't block on loading - render immediately
  // If user exists and authenticated, redirect to home
  // Otherwise show login/public page immediately

  // Allow password recovery users to access password reset page
  if (user && !isPasswordRecovery && !loading) {
    return <Navigate to="/" replace />; // Redirect to home/dashboard if logged in
  }

  return children;
};

const InvitationRouteWrapper: React.FC<ProtectedRouteProps> = ({ children }) => {
  // No auth checks needed - SecureInvitationHandler handles everything independently
  // This allows invitation page to render immediately without waiting for auth context
  return children;
};

// Component to set up global auth error handler
const AuthErrorHandlerSetup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { handleAuthError } = useAuth();
  
  React.useEffect(() => {
    // Set up the global auth error handler for Edge Function calls
    setupGlobalAuthErrorHandler(handleAuthError);
    // Global auth error handler configured
    
    return () => {
      // Clean up on unmount (though this probably won't happen in practice)
      setupGlobalAuthErrorHandler(() => {});
    };
  }, [handleAuthError]);

  return <>{children}</>;
};

// Translation management wrapper component
const TranslationManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    showBanner,
    browserLanguage,
    browserLanguageDisplayName,
    isProcessingChoice,
    showWarningBadge,
    isTranslationActive,
    detectedTranslationLanguage,
    isDisablingTranslation,
    handleTranslationChoice,
    closeBanner,
    disableTranslation,
    isInitialized,
  } = useTranslationManager();

  return (
    <>
      {children}
      {isInitialized && (
        <>
          {/* Translation Choice Banner */}
          {showBanner && browserLanguage && (
            <TranslationChoiceBanner
              browserLanguage={browserLanguage}
              browserLanguageDisplayName={browserLanguageDisplayName}
              isVisible={showBanner}
              onChoice={handleTranslationChoice}
              onClose={closeBanner}
              isProcessing={isProcessingChoice}
            />
          )}

          {/* Translation Warning Badge */}
          {showWarningBadge && (
            <TranslationWarningBadge
              isTranslationActive={isTranslationActive}
              detectedLanguage={detectedTranslationLanguage ?? 'unknown'}
              onDisableTranslation={disableTranslation}
              isProcessing={isDisablingTranslation}
            />
          )}
        </>
      )}
    </>
  );
};

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}> {/* Wrap with QueryClientProvider */}
        <UpdateBanner />
        <EnvironmentIndicator />
        <ThemeProvider defaultTheme='light' storageKey='vite-ui-theme'>
          <FontProvider>
            <SearchProvider>
              <SidebarProvider defaultOpen={true}>
                <UsersProvider>
                  <AuthProvider> {/* AuthProvider needs to wrap routes that use useAuth */}
                    <PermissionProvider> {/* PermissionProvider needs user context from AuthProvider */}
                      <AuthErrorHandlerSetup>
                        <TranslationManager>
                          <Routes>
                            {/* Public routes only (e.g., login, password reset) */}
                            <Route path="/login" element={<PublicRouteOnly><Login /></PublicRouteOnly>} />
                            <Route path="/request-password-reset" element={<PublicRouteOnly><RequestPasswordResetPage /></PublicRouteOnly>} />
                            <Route path="/password-reset" element={<PublicRouteOnly><PasswordResetPage /></PublicRouteOnly>} />
                            <Route path="/invitation/pending" element={<PublicRouteOnly><InvitationPendingPage /></PublicRouteOnly>} /> {/* Handle invitation pending state */}
                            <Route path="/invitation/:token" element={<InvitationRouteWrapper><InvitationPage /></InvitationRouteWrapper>} /> {/* Invitation completion */}
                            <Route path="/email-verification" element={<EmailVerificationPage />} /> {/* Email verification can be accessed anytime */}

                            {/* Protected Routes - Require Authentication */}
                            {/* MainLayout will contain the sidebar and other layout elements */}
                            <Route
                              path="/*" // All other routes will be handled by MainLayout
                              element={
                                <ProtectedRoute>
                                  <MainLayout />
                                </ProtectedRoute>
                              }
                            />
                          </Routes>
                        </TranslationManager>
                      </AuthErrorHandlerSetup>
                    </PermissionProvider>
                  </AuthProvider>
                </UsersProvider>
              </SidebarProvider>
            </SearchProvider>
          </FontProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  );
};

// This component will be rendered by main.tsx as the root
const App: React.FC = () => {
  return <AppRouter />;
};

export default App; 